import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Sparkles, Send, Lightbulb, BookOpen } from 'lucide-react';
import { cpdAPI, errMsg } from '../../lib/cpdRegisterAPI';
import { humanizeEnums } from './CPDStatus';
import { toast } from 'sonner';

const QUESTIONS = ['Am I compliant with TPB?', 'Am I compliant with CPA?', 'How many hours do I need this year?', 'What ethics hours am I missing?',
  'Which activities have no evidence?', 'What should I complete before year end?', 'Prepare my CPD summary for a TPB review', 'What mandatory training is outstanding?'];

export const CPDRecommendations = ({ userId, refreshKey }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { cpdAPI.recommendations(userId).then((r) => setItems(r.items)).catch(() => {}); }, [userId, refreshKey]);
  if (!items.length) return null;
  return (
    <Card data-testid="cpd-recommendations"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Recommendations</CardTitle></CardHeader>
      <CardContent className="space-y-1.5 text-sm">{items.map((r, i) => <p key={i}><b>{r.framework}:</b> {r.text}</p>)}
        <p className="text-xs text-muted-foreground">Informational only – recommendations never change compliance status.</p></CardContent></Card>
  );
};

export const CPDAssistant = ({ open, onOpenChange, me }) => {
  const [q, setQ] = useState('');
  const [log, setLog] = useState([]);
  const [st, setSt] = useState(null);
  useEffect(() => { if (open && !st) cpdAPI.assistStatus().then(setSt).catch(() => {}); }, [open, st]);
  const ask = async (text) => {
    if (!text.trim()) return;
    setQ('');
    try { const r = await cpdAPI.ask(text); setLog((l) => [...l, { q: text, ...r }]); } catch (e) { setLog((l) => [...l, { q: text, error: errMsg(e) }]); toast.error(errMsg(e)); }
  };
  const qs = me.tier === 'employee' ? QUESTIONS : [...QUESTIONS, 'Which employees are at risk?'];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="cpd-assistant">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> CPD Q&A</SheetTitle></SheetHeader>
        <p className="text-xs text-muted-foreground mt-1" data-testid="cpd-assistant-mode">{st?.available ? 'AI rephrasing available' : 'AI not configured – answers come from your CPD records and rules only.'}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">{qs.map((x) => <button key={x} onClick={() => ask(x)} className="text-xs border rounded-full px-2.5 py-1 hover:bg-slate-50" data-testid={`cpd-q-${x.slice(0, 18).replace(/\W+/g, '-').toLowerCase()}`}>{x}</button>)}</div>
        <div className="space-y-4 mt-4">{log.map((m, i) => (
          <div key={i} className="space-y-1" data-testid={`cpd-answer-${i}`}><p className="text-sm font-semibold">{m.q}</p>
            {m.error ? <p className="text-sm text-rose-700">{m.error}</p> : <>
              <ul className="text-sm space-y-0.5">{m.answer.map((a, j) => <li key={j}>• {humanizeEnums(a)}</li>)}</ul>
              <div className="flex flex-wrap gap-1.5 pt-1">{m.citations.map((c, j) => <a key={j} href={c.link} className="text-xs text-[#0055FF] inline-flex items-center gap-1"><BookOpen className="w-3 h-3" />{c.label}</a>)}</div></>}
          </div>))}</div>
        <form className="flex gap-2 mt-4" onSubmit={(e) => { e.preventDefault(); ask(q); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about your CPD" data-testid="cpd-assistant-input" />
          <Button type="submit" size="icon" data-testid="cpd-assistant-send"><Send className="w-4 h-4" /></Button></form>
      </SheetContent>
    </Sheet>
  );
};
